import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { toast } from '../../hooks/use-toast';
import { ShieldPlus, ArrowLeft, CreditCard, Stethoscope, FileSpreadsheet, FileText, Download, Eye, Calendar, ChevronDown, ChevronUp, Users } from 'lucide-react';
import SpreadsheetViewer from '../../components/employee-portal/SpreadsheetViewer';

const API = process.env.REACT_APP_BACKEND_URL;

const formatDate = (d) => {
  if (!d) return '';
  try { return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return d; }
};

const isImage = (filename) => /\.(jpe?g|png|webp)$/i.test(filename || '');
const isPdf = (filename, ext) => /\.pdf$/i.test(filename || '') || (ext || '').toLowerCase() === '.pdf';
const isExcel = (filename, ext) => /\.(xlsx?|csv)$/i.test(filename || '') || ['.xlsx', '.xls', '.csv'].includes((ext || '').toLowerCase());

const InlinePdf = ({ src, testId, boxClass = 'h-[420px]', aspectRatio }) => (
  <div
    className={`w-full rounded-lg border bg-gray-50 overflow-hidden ${aspectRatio ? '' : boxClass}`}
    style={aspectRatio ? { aspectRatio: String(aspectRatio), maxHeight: '70vh' } : undefined}
  >
    <object
      data={`${src}#toolbar=0&navpanes=0&view=Fit`}
      type="application/pdf"
      className="w-full h-full block"
      data-testid={testId}
    >
      <iframe
        src={`${src}#toolbar=0&navpanes=0&view=Fit`}
        title="document"
        className="w-full h-full"
      />
      <p className="text-xs text-gray-500 p-3">
        PDF preview not supported on this device. <a href={src} target="_blank" rel="noreferrer" className="text-emerald-600 underline">Tap to open</a>.
      </p>
    </object>
  </div>
);

const PortalInsurance = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('portal_token');
  const headers = { Authorization: `Bearer ${token}` };

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openFiles, setOpenFiles] = useState({}); // {file_id: bool}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/insurance/portal/me`, { headers });
      setData(r.data);
      // Auto-open first global file by default
      const first = (r.data?.global_files || [])[0];
      if (first) setOpenFiles({ [first.id]: true });
    } catch (e) {
      if (e?.response?.status === 401) navigate('/employee-portal/login');
      toast({ title: 'Failed to load', variant: 'destructive' });
    } finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const card = data?.card;
  const coverage = data?.coverage;
  const dependents = data?.dependents || [];
  const globalFiles = data?.global_files || [];
  const globalTitle = data?.global_title || 'Insurance & Hospital Coverage';

  const togglePreview = (id) => setOpenFiles(o => ({ ...o, [id]: !o[id] }));

  const renderInline = (url, filename, ext, fileId, color = 'emerald', opts = {}) => {
    if (isImage(filename)) {
      return (
        <a href={`${API}${url}`} target="_blank" rel="noreferrer" className="block">
          <img
            src={`${API}${url}`}
            alt={filename}
            className="w-full h-auto rounded-lg border bg-white"
            data-testid={`inline-img-${fileId || ''}`}
          />
        </a>
      );
    }
    if (isPdf(filename, ext)) {
      return <InlinePdf src={`${API}${url}`} testId={`inline-pdf-${fileId || ''}`} boxClass={opts.pdfBoxClass} aspectRatio={opts.aspectRatio} />;
    }
    if (isExcel(filename, ext) && fileId) {
      return <SpreadsheetViewer fileId={fileId} filename={filename} />;
    }
    // Fallback for unknown types
    return (
      <div className={`rounded-lg border bg-${color}-50/40 p-6 text-center`}>
        <FileSpreadsheet className="h-10 w-10 mx-auto text-gray-300 mb-2" />
        <p className="text-sm font-medium truncate">{filename}</p>
      </div>
    );
  };

  return (
    <div className="p-4 max-w-2xl mx-auto" data-testid="portal-insurance">
      <button onClick={() => navigate('/employee-portal')} className="flex items-center gap-1 text-sm text-violet-600 mb-4">
        <ArrowLeft className="h-4 w-4" /> Back to Home
      </button>

      <div className="flex items-center gap-3 mb-5">
        <div className="h-12 w-12 rounded-full bg-emerald-100 flex items-center justify-center">
          <ShieldPlus className="h-6 w-6 text-emerald-600" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900">My Insurance</h1>
          <p className="text-xs text-gray-500">Health coverage, hospital list & policy details</p>
        </div>
      </div>

      {loading ? (
        <Card><CardContent className="py-12 text-center text-gray-500">Loading…</CardContent></Card>
      ) : (
        <div className="space-y-4">
          {/* My Insurance Card */}
          <Card data-testid="my-card-section" className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <CreditCard className="h-5 w-5 text-sky-600" />
                <h2 className="text-base font-semibold">Insurance Card</h2>
                {card ? (
                  <Badge className="bg-emerald-100 text-emerald-700 text-[10px] ml-auto">
                    <Calendar className="h-3 w-3 mr-0.5" /> {formatDate(card.uploaded_at)}
                  </Badge>
                ) : (
                  <Badge className="bg-amber-100 text-amber-700 text-[10px] ml-auto">Not uploaded</Badge>
                )}
              </div>
              {card?.url ? (
                <>
                  {renderInline(card.url, card.filename, null, null, 'sky', { aspectRatio: card.aspect_ratio || 1.6 })}
                  <div className="flex gap-2 mt-3">
                    <a
                      href={`${API}${card.url}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded text-xs bg-sky-50 text-sky-700 border border-sky-200 hover:bg-sky-100"
                      data-testid="card-view-btn"
                    >
                      <Eye className="h-3.5 w-3.5" /> View
                    </a>
                    <a
                      href={`${API}${card.url}`}
                      download={card.filename}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded text-xs bg-sky-600 text-white hover:bg-sky-700"
                      data-testid="card-download-btn"
                    >
                      <Download className="h-3.5 w-3.5" /> Download
                    </a>
                  </div>
                </>
              ) : (
                <p className="text-xs text-gray-500 italic">Your insurance card will appear here once HR uploads it.</p>
              )}
            </CardContent>
          </Card>

          {/* My Coverage Doc — only shown when uploaded (global Network List covers everyone) */}
          {coverage?.url && (
            <Card data-testid="my-coverage-section" className="border-0 shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Stethoscope className="h-5 w-5 text-violet-600" />
                  <h2 className="text-base font-semibold">Hospital Coverage</h2>
                  <Badge className="bg-emerald-100 text-emerald-700 text-[10px] ml-auto">
                    <Calendar className="h-3 w-3 mr-0.5" /> {formatDate(coverage.uploaded_at)}
                  </Badge>
                </div>
                {renderInline(coverage.url, coverage.filename, null, null, 'violet')}
                <div className="flex gap-2 mt-3">
                  <a
                    href={`${API}${coverage.url}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded text-xs bg-violet-50 text-violet-700 border border-violet-200 hover:bg-violet-100"
                  >
                    <Eye className="h-3.5 w-3.5" /> View
                  </a>
                  <a
                    href={`${API}${coverage.url}`}
                    download={coverage.filename}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded text-xs bg-violet-600 text-white hover:bg-violet-700"
                  >
                    <Download className="h-3.5 w-3.5" /> Download
                  </a>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Dependents' Insurance Cards */}
          {dependents.length > 0 && (
            <Card data-testid="my-dependents-section" className="border-0 shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Users className="h-5 w-5 text-emerald-600" />
                  <h2 className="text-base font-semibold">Dependents' Insurance Cards</h2>
                  <Badge className="bg-emerald-100 text-emerald-700 text-[10px] ml-auto">
                    {dependents.length}
                  </Badge>
                </div>
                <div className="space-y-4">
                  {dependents.map((dep) => {
                    const dcard = (dep.insurance_documents || {}).card;
                    const dcov = (dep.insurance_documents || {}).coverage;
                    return (
                      <div key={dep.id} className="rounded-lg border bg-white p-3" data-testid={`dep-card-${dep.id}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <CreditCard className="h-4 w-4 text-sky-600" />
                          <span className="text-sm font-semibold">{dep.name}</span>
                          {dep.relation && (
                            <span className="text-[11px] text-gray-500">· {dep.relation}</span>
                          )}
                        </div>
                        {dcard?.url ? (
                          <>
                            {renderInline(dcard.url, dcard.filename, dcard.ext, null, 'sky', { aspectRatio: dcard.aspect_ratio || 1.6 })}
                            <div className="flex gap-2 mt-3">
                              <a href={`${API}${dcard.url}`} target="_blank" rel="noreferrer"
                                 className="inline-flex items-center gap-1 px-3 py-1.5 rounded text-xs bg-sky-50 text-sky-700 border border-sky-200 hover:bg-sky-100"
                                 data-testid={`dep-view-${dep.id}`}>
                                <Eye className="h-3.5 w-3.5" /> View
                              </a>
                              <a href={`${API}${dcard.url}`} download={dcard.filename}
                                 className="inline-flex items-center gap-1 px-3 py-1.5 rounded text-xs bg-sky-600 text-white hover:bg-sky-700"
                                 data-testid={`dep-download-${dep.id}`}>
                                <Download className="h-3.5 w-3.5" /> Download
                              </a>
                            </div>
                          </>
                        ) : (
                          <p className="text-xs text-gray-500 italic">No card uploaded yet.</p>
                        )}
                        {dcov?.url && (
                          <div className="mt-3">
                            {renderInline(dcov.url, dcov.filename, dcov.ext, null, 'violet', { aspectRatio: dcov.aspect_ratio || 1.6 })}
                            <div className="flex gap-2 mt-3">
                              <a href={`${API}${dcov.url}`} target="_blank" rel="noreferrer"
                                 className="inline-flex items-center gap-1 px-3 py-1.5 rounded text-xs bg-violet-50 text-violet-700 border border-violet-200 hover:bg-violet-100">
                                <Eye className="h-3.5 w-3.5" /> View
                              </a>
                              <a href={`${API}${dcov.url}`} download={dcov.filename}
                                 className="inline-flex items-center gap-1 px-3 py-1.5 rounded text-xs bg-violet-600 text-white hover:bg-violet-700">
                                <Download className="h-3.5 w-3.5" /> Download
                              </a>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Global Coverage */}
          <Card data-testid="global-coverage-section" className="border-0 shadow-sm bg-gradient-to-br from-emerald-50 to-teal-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <FileSpreadsheet className="h-5 w-5 text-emerald-600" />
                <h2 className="text-base font-semibold">{globalTitle}</h2>
                {globalFiles.length > 0 && (
                  <Badge className="bg-emerald-100 text-emerald-700 text-[10px] ml-auto">
                    {globalFiles.length} file{globalFiles.length === 1 ? '' : 's'}
                  </Badge>
                )}
              </div>
              {globalFiles.length > 0 ? (
                <div className="space-y-3">
                  {globalFiles.map(g => {
                    const isOpen = !!openFiles[g.id];
                    const Icon = isPdf(g.filename, g.ext) ? FileText : FileSpreadsheet;
                    return (
                      <div key={g.id} className="rounded-lg border bg-white" data-testid={`global-file-${g.id}`}>
                        <div className="p-3 flex items-start gap-3">
                          <Icon className={`h-7 w-7 mt-0.5 flex-shrink-0 ${isPdf(g.filename, g.ext) ? 'text-rose-500' : 'text-emerald-600'}`} />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold truncate" data-testid={`global-title-${g.id}`}>
                              {g.title || g.filename}
                            </p>
                            {g.title && g.title !== g.filename && (
                              <p className="text-[11px] text-gray-500 truncate">{g.filename}</p>
                            )}
                            <p className="text-[11px] text-gray-500 mt-0.5 flex items-center gap-1">
                              <Calendar className="h-3 w-3" /> Updated {formatDate(g.uploaded_at)}
                            </p>
                          </div>
                          <button
                            onClick={() => togglePreview(g.id)}
                            className="p-1 rounded hover:bg-emerald-50"
                            aria-label="toggle preview"
                            data-testid={`global-toggle-${g.id}`}
                          >
                            {isOpen ? <ChevronUp className="h-4 w-4 text-emerald-700" /> : <ChevronDown className="h-4 w-4 text-emerald-700" />}
                          </button>
                        </div>

                        {isOpen && (
                          <div className="px-3 pb-3" data-testid={`global-preview-${g.id}`}>
                            {renderInline(g.url, g.filename, g.ext, g.id)}
                          </div>
                        )}

                        <div className="flex gap-2 px-3 pb-3 border-t pt-2">
                          <a
                            href={`${API}${g.url}`}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded text-xs bg-white border border-emerald-200 hover:bg-emerald-50 text-emerald-700"
                            data-testid={`global-view-${g.id}`}
                          >
                            <Eye className="h-3.5 w-3.5" /> Open in new tab
                          </a>
                          <a
                            href={`${API}${g.url}`}
                            download={g.filename}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded text-xs bg-emerald-600 text-white hover:bg-emerald-700"
                            data-testid={`global-download-${g.id}`}
                          >
                            <Download className="h-3.5 w-3.5" /> Download
                          </a>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-xs text-gray-600 italic">Company-wide insurance files will appear here once HR uploads them.</p>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default PortalInsurance;
